import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { ProductDetailsInputValidationSchema } from '../product/create';
import {
  BasePageFieldsInputValidationSchema,
  BaseCheckoutAndEventFieldsInputValidationSchema,
} from '../page/create';
import { withTaxModeRefinements } from '../page/common/tax';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';

export const CreateCheckoutPageBaseInputValidationSchema =
  BasePageFieldsInputValidationSchema.extend(BaseCheckoutAndEventFieldsInputValidationSchema.shape)
    .extend({
      productData: ProductDetailsInputValidationSchema.nullish(),
      // This will be a future enhancement to allow the merchant to set a specific
      // productId: z.string(),
      tax: withTaxModeRefinements(
        z.object({
          enabled: z.boolean().meta({
            description:
              'Whether to collect tax. When `true`, `mode` must be `fixed` or `stripe`. When `false`, `mode` must be `none` or omitted.',
          }),
          mode: z.enum(['none', 'fixed', 'stripe']).optional().meta({
            description:
              "Tax collection mode. `stripe` — Stripe Tax calculates tax automatically based on the customer's address; requires address fields on the page and Stripe Tax configured in the account. `fixed` — a fixed tax rate is applied; configure the rate ID on `productData.fixedTaxRateIds` or rely on the account default rate. Call `list_tax_rates` first to find an existing rate. If no suitable rate exists, use `create_tax_rate` to create one first. `none` — no tax is collected. Defaults to `stripe` when `enabled` is `true`.",
          }),
        }),
      )
        .nullish()
        .meta({
          description: 'Tax configuration for this page. Defaults to Stripe Tax when omitted.',
          example: { enabled: true, mode: 'stripe' },
        }),
      enableFileAccessForInactiveSubscriptions: z.boolean().nullish().meta({
        description:
          "Deprecated — superseded by `productData.enableFileAccessForInactiveSubscriptions`. Accepted and applied to the page's product.",
        example: false,
        deprecated: true,
      }),
      limitSubscriptions: z
        .object({
          enabled: z.boolean().nullish(),
        })
        .nullish()
        .meta({
          description:
            "Deprecated — superseded by `productData.limitSubscriptions.enabled`. Accepted and applied to the page's product.",
          deprecated: true,
        }),
      limitSubscriptionsStripe: z
        .object({
          enabled: z.boolean().nullish(),
        })
        .nullish()
        .meta({
          description:
            "Deprecated — superseded by `productData.limitSubscriptions.limitSubscriptionsStripe.enabled`. Accepted and applied to the page's product.",
          deprecated: true,
        }),
      sendCanceledSubscriptionNotifications: z.boolean().nullish().meta({
        description: 'Send notifications when subscriptions are canceled.',
        example: true,
      }),
      sendRecurringPaymentFailedNotifications: z.boolean().nullish().meta({
        description: 'Send notifications when recurring payments fail.',
        example: true,
      }),
      sendRecurringPaymentSucceededNotifications: z.boolean().nullish().meta({
        description: 'Send notifications when recurring payments succeed.',
        example: true,
      }),
      allowDynamicPlanIterations: z.boolean().nullish().meta({
        description:
          'Allow subscription plan iterations to be overridden via URL query parameters.',
        example: false,
      }),
      imageIds: z.array(ObjectIdStringValidationSchema).optional().meta({
        description:
          'Array of images for the page gallery. All referenced file IDs must exist and belong to your account.',
      }),
    })
    .meta({ id: 'CreateCheckoutPageBaseInput' });

export const CreateCheckoutPageInputValidationSchema = CreateCheckoutPageBaseInputValidationSchema;

// MCP callers cannot set `status` — every page they create goes live immediately.
// MCP advertises themeOverrides loose; see ThemeOverridesToolInputValidationSchema.
export const CreateCheckoutPageInputWithoutStatusValidationSchema =
  CreateCheckoutPageBaseInputValidationSchema.omit({ status: true }).extend({
    themeOverrides: ThemeOverridesToolInputValidationSchema,
  });

export type CreateCheckoutPageInput = z.infer<typeof CreateCheckoutPageInputValidationSchema>;
